import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FiArrowUpRight, FiChevronDown } from "react-icons/fi";
import {
  agentWorkflows,
  aiPracticeLinks,
  managedCare,
  solutionEvidence,
  solutionLinkNames,
  solutionsProcess,
  voicesSolutionsData,
} from "./voicesSolutionsData";
import "./voices-solutions.css";
import VoicesInteriorScene, { interiorSceneType } from './VoicesInteriorScene';

export { voicesSolutionsMetadata } from "./voicesSolutionsData";

function ContactLink({ children, secondary = false }) {
  return <Link className={secondary ? "vs-text-link" : "vs-contact-link"} to="/contact">
    {children}<FiArrowUpRight aria-hidden="true" />
  </Link>;
}

function RelatedLinks({ paths, title = "Related services" }) {
  return <nav className="vs-related" aria-label={title}>
    {paths.map((path) => <Link className="vs-text-link" key={path} to={path}>
      {voicesSolutionsData[path]?.name || solutionLinkNames[path]}
      <FiArrowUpRight aria-hidden="true" />
    </Link>)}
  </nav>;
}

function Capabilities({ page }) {
  return <section className="vs-band vs-band-light" data-voices-theme="dark" id="vs-capabilities" aria-labelledby="vs-capabilities-title">
    <div className="vs-inner vs-section-layout">
      <div>
        <p className="vs-label">{page.type === "agents" ? "Built around the job" : "The work behind the experience"}</p>
        <h2 id="vs-capabilities-title">{page.type === "agents" ? <>Intelligence. <br /><em>With intention.</em></> : <>Our key <br /><em>capabilities.</em></>}</h2>
      </div>
      <ol className="vs-numbered-list">
        {page.capabilities.map((item, index) => <li className="vs-numbered-item" key={index}>
          <span className="vs-number" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
          <div>{Array.isArray(item) ? <p><strong>{item[0]}.</strong> {item[1]}</p> : <p>{item}</p>}</div>
        </li>)}
      </ol>
    </div>
  </section>;
}

function WorkflowChoices() {
  const [selection, setSelection] = useState(0);
  const workflow = agentWorkflows[selection];
  return <section className="vs-band vs-band-soft" id="vs-workflows" aria-labelledby="vs-workflows-title">
    <div className="vs-inner vs-section-layout">
      <div>
        <p className="vs-label">Workflow planning</p>
        <h2 id="vs-workflows-title">One useful job. Done thoughtfully.</h2>
        <p>Not everything should happen without a person.</p>
      </div>
      <div className="vs-workflow">
        <fieldset className="vs-workflow-choices">
          <legend>Business workflow</legend>
          <div className="vs-segments">
            {agentWorkflows.map((item, index) => <label className="vs-segment" key={item.name}>
              <input type="radio" name="vs-workflow" value={item.name} checked={selection === index} onChange={() => setSelection(index)} aria-controls="vs-workflow-outline" />
              <span>{item.name}</span>
            </label>)}
          </div>
        </fieldset>
        <div className="vs-workflow-outline" id="vs-workflow-outline" aria-live="polite" aria-atomic="true">
          <h3>{workflow.request}</h3>
          <ol className="vs-workflow-steps">
            {workflow.steps.map((step) => <li key={step}>{step}</li>)}
          </ol>
        </div>
        <p className="vs-note">An illustrative walkthrough, not a live AI or Salesforce connection. No real records are read or changed.</p>
        <ContactLink secondary>Design your agent</ContactLink>
      </div>
    </div>
  </section>;
}

function ManagedCare({ link = false }) {
  return <section className="vs-band vs-band-soft" id="vs-care" aria-labelledby="vs-care-title">
    <div className="vs-inner vs-section-layout">
      <div>
        <p className="vs-label">The long game</p>
        <h2 id="vs-care-title">{managedCare.title}</h2>
        <p>{managedCare.description}</p>
        {link && <Link className="vs-text-link" to="/managed-agents">Meet your managed-agent team<FiArrowUpRight aria-hidden="true" /></Link>}
      </div>
      <div className="vs-care-list">
        {managedCare.items.map(([title, description]) => <div className="vs-care-item" key={title}>
          <h3>{title}</h3><p>{description}</p>
        </div>)}
      </div>
    </div>
  </section>;
}

function Evidence() {
  return <section className="vs-band vs-band-light" data-voices-theme="dark" id="vs-evidence" aria-labelledby="vs-evidence-title">
    <div className="vs-inner">
      <div className="vs-section-heading">
        <p className="vs-label">Evidence before promises</p>
        <h2 id="vs-evidence-title">Read the work. <br /><em>Then talk about yours.</em></h2>
        <p>Explore Antrazal&apos;s documented Salesforce and integration work. These stories describe their own technologies and scope; they are not presented as OpenAI or Claude implementations.</p>
      </div>
      <ul className="vs-evidence-list">
        {solutionEvidence.map((story) => <li key={story.path}>
          <Link className="vs-evidence-link" to={story.path}>
            <div className="vs-evidence-copy">
              <h3>{story.name}</h3><p>{story.description}</p>
              <FiArrowUpRight aria-hidden="true" />
            </div>
          </Link>
        </li>)}
      </ul>
    </div>
  </section>;
}

export function Process() {
  return <section className="vs-band vs-process" id="vs-process" aria-labelledby="vs-process-title">
    <div className="vs-inner">
      <p className="vs-label">How we build</p>
      <h2 id="vs-process-title">A shared path. Clear ownership.</h2>
      <ol className="vs-process-list">
        {solutionsProcess.map(([title, description], index) => <li key={title}>
          <span className="vs-number" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
          <h3>{title}</h3><p>{description}</p>
        </li>)}
      </ol>
    </div>
  </section>;
}

function ServiceContent({ page }) {
  return <>
    <section className="vs-band vs-scope" aria-label="Engagement scope"><div className="vs-inner"><p>{page.scope}</p></div></section>
    <Capabilities page={page} />
    <ServiceEngagement page={page} />
    <Process />
  </>;
}

export function ServiceEngagement({ page }) {
  return <>
    <section className="vs-band vs-band-soft" id="vs-deliverables" aria-labelledby="vs-deliverables-title">
      <div className="vs-inner">
        <p className="vs-label">A clear engagement</p>
        <h2 id="vs-deliverables-title">Know what <br /><em>you are building toward.</em></h2>
        <div className="vs-item-grid">
          {page.deliverables.map(([title, description], index) => <article className="vs-item" key={title}>
            <span className="vs-number" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
            <h3>{title}</h3><p>{description}</p>
          </article>)}
        </div>
      </div>
    </section>
    <Evidence />
    <section className="vs-band" id="vs-questions" aria-labelledby="vs-questions-title">
      <div className="vs-inner vs-section-layout">
        <div><p className="vs-label">Before we start</p><h2 id="vs-questions-title">Good questions. <br /><em>Clear answers.</em></h2></div>
        <div>
          {page.questions.map(([question, answer]) => <details className="vs-question" key={question}>
            <summary className="vs-question-summary">{question}<FiChevronDown aria-hidden="true" /></summary>
            <p>{answer}</p>
          </details>)}
          <RelatedLinks paths={page.relatedIntent} />
        </div>
      </div>
    </section>
  </>;
}

function AgentsContent({ page }) {
  return <>
    <Capabilities page={page} />
    <WorkflowChoices />
    <section className="vs-band" id="vs-practice" aria-labelledby="vs-practice-title">
      <div className="vs-inner vs-section-layout">
        <div><p className="vs-label">The AI practice</p><h2 id="vs-practice-title">AI services</h2></div>
        <ul className="vs-service-list">
          {aiPracticeLinks.map(([path, name]) => <li key={path}><Link className="vs-text-link" to={path}>{name}<FiArrowUpRight aria-hidden="true" /></Link></li>)}
        </ul>
      </div>
    </section>
    <Process />
    <ManagedCare link />
    <Evidence />
  </>;
}

function ManagedContent({ page }) {
  return <>
    <ManagedCare />
    <section className="vs-band" id="vs-engagements" aria-labelledby="vs-engagements-title">
      <div className="vs-inner">
        <div className="vs-section-heading">
          <p className="vs-label">Care that scales with the work</p>
          <h2 id="vs-engagements-title">The right team. The right commitment.</h2>
          <p>{page.engagementIntro}</p>
        </div>
        <div className="vs-item-grid">
          {page.engagements.map(([title, description]) => <article className="vs-item" key={title}>
            <h3>{title}</h3><p>{description}</p><ContactLink secondary>Discuss the scope</ContactLink>
          </article>)}
        </div>
        <p className="vs-note">{page.terms}</p>
      </div>
    </section>
    <section className="vs-band vs-band-light" data-voices-theme="dark" id="vs-ownership" aria-labelledby="vs-ownership-title">
      <div className="vs-inner vs-section-layout">
        <div><p className="vs-label">Visible responsibility</p><h2 id="vs-ownership-title">Less uncertainty. More ownership.</h2></div>
        <ul className="vs-responsibilities">{page.capabilities.map((item) => <li key={item}>{item}</li>)}</ul>
      </div>
    </section>
    <Process />
    <Evidence />
  </>;
}

function GuideContent({ page }) {
  return <>
    <section className="vs-band vs-band-light" data-voices-theme="dark" id="vs-criteria" aria-label="Choosing an implementation partner">
      <div className="vs-inner vs-section-layout">
        <aside className="vs-guide-aside">
          <nav aria-label="Partner selection criteria">
            {page.criteria.map(([title], index) => <a className="vs-guide-link" key={title} href={`#vs-criterion-${index + 1}`}>{index + 1}. {title}</a>)}
          </nav>
          <ContactLink secondary>Discuss your use case</ContactLink>
        </aside>
        <article className="vs-guide-body">
          {page.criteria.map(([title, description], index) => <section className="vs-guide-section" key={title} id={`vs-criterion-${index + 1}`}>
            <h2>{index + 1}. {title}</h2><p>{description}</p>
          </section>)}
          {page.sections.map(([title, description], index) => <section className="vs-guide-section" key={title} id={index === 0 ? "vs-platform" : "vs-brief"}>
            <h2>{title}</h2><p>{description}</p>
          </section>)}
          <RelatedLinks paths={page.relatedIntent} />
          <p className="vs-note">{page.note}</p>
        </article>
      </div>
    </section>
    <Evidence />
  </>;
}

const sectionLinks = {
  service: [["capabilities", "Capabilities"], ["deliverables", "Deliverables"], ["evidence", "Customer work"], ["questions", "Questions"], ["process", "How we build"]],
  agents: [["capabilities", "Capabilities"], ["workflows", "Workflows"], ["practice", "AI services"], ["process", "How we build"], ["care", "Ongoing care"]],
  managed: [["care", "Ongoing care"], ["engagements", "Engagements"], ["ownership", "Ownership"], ["process", "How we build"]],
  guide: [["criteria", "Selection criteria"], ["platform", "Platform choice"], ["brief", "Your brief"], ["evidence", "Customer work"]],
};

function SolutionPage({ page, hash }) {
  const mainRef = useRef(null);
  const Hero = page.type === 'guide' ? 'header' : VoicesInteriorScene;
  const heroTitle = page.type === 'guide' ? 'The best fit is backed by evidence.' : page.type === 'agents' || page.type === 'managed' ? page.headline : page.name;
  const breakAt = page.type === 'agents' ? 'around you.' : page.type === 'managed' ? 'We stay with it.' : page.type === 'service' ? 'Implementation' : 'is backed by evidence.';
  const titleStart = breakAt ? heroTitle.lastIndexOf(breakAt) : -1;

  useEffect(() => {
    if (!hash.startsWith("#vs-")) return;
    const frame = requestAnimationFrame(() => {
      let id;
      try { id = decodeURIComponent(hash.slice(1)); } catch { return; }
      const target = document.getElementById(id);
      if (target && mainRef.current?.contains(target)) target.scrollIntoView({ block: "start" });
    });
    return () => cancelAnimationFrame(frame);
  }, [hash]);

  return <main className={`vs-page vs-page--${page.type}`} data-voices-theme="dark" id="voices-main" ref={mainRef} aria-label={page.name}>
    <Hero className={`vs-intro${page.type === 'guide' ? ' vs-guide-intro' : ''}`} {...(page.type === 'guide' ? {} : { scene: interiorSceneType(page) })}>
      <div className="vs-inner">
        <nav className="vs-breadcrumb" aria-label="Breadcrumb"><Link to="/">Home</Link><span aria-hidden="true">/</span><Link to="/services">Services</Link><span aria-hidden="true">/</span><span aria-current="page">{page.name}</span></nav>
        <p className="vs-label">{page.category}</p>
        <h1 id="vs-page-title">{titleStart > 0 ? <>{heroTitle.slice(0, titleStart)}<br /><em>{heroTitle.slice(titleStart)}</em></> : heroTitle}</h1>
        {page.type === 'service' && <p className="vs-headline">{page.headline}</p>}
        {page.type !== "service" && <p className="vs-description">{page.description}</p>}
        <ContactLink>{page.cta}</ContactLink>
      </div>
    </Hero>
    <nav className="vs-section-nav" aria-label="On this page"><div className="vs-inner">
      {sectionLinks[page.type].map(([id, title]) => <a className="vs-section-link" key={id} href={`#vs-${id}`}>{title}</a>)}
    </div></nav>
    {page.type === "service" && <ServiceContent page={page} />}
    {page.type === "agents" && <AgentsContent page={page} />}
    {page.type === "managed" && <ManagedContent page={page} />}
    {page.type === "guide" && <GuideContent page={page} />}
  </main>;
}

// The app supplies the existing Voices shell and owns public-route/SEO registration.
export default function VoicesSolutions({ pathname }) {
  const location = useLocation();
  let path;
  try {
    path = decodeURIComponent((pathname ?? location.pathname).split(/[?#]/)[0]).toLowerCase().replace(/\/+$/, "");
  } catch {
    return null;
  }
  const page = voicesSolutionsData[path];
  return page ? <SolutionPage key={path} page={page} hash={location.hash} /> : null;
}
